import React, { useState, useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import styled, { withTheme } from "styled-components";
import { Row, Col } from "react-bootstrap";
import {
  profiles,
  emailRegex,
  Sites,
  FieldsValidation,
} from "../../../modules/constants";
import { RegistrationType } from "./AddUserAccessManagement.constants";
import AccentButton from "../../ui-library/Buttons/AccentButton/AccentButton";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import CustomDropdown from "../../ui-library/CustomDropdown/CustomDropdown";
import AutoFocusInput from "../../ui-library/AutoFocusInput/AutoFocusInput";
import { useTranslation } from "react-i18next";
import { isEmailValid } from "../../../modules/utils";

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 38px;
  @media (max-width: 576px) {
    padding-top: 2rem;
    margin-top: auto;
    .btn {
      width: 100%;
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const RolesWrapper = styled.div`
  [type="radio"]:checked,
  [type="radio"]:not(:checked) {
    position: absolute;
    left: -9999px;
  }

  [type="radio"]:checked + label,
  [type="radio"]:not(:checked) + label {
    position: relative;
    padding-left: 50px;
    cursor: pointer;
    line-height: 28px;
    display: inline-block;
  }

  [type="radio"]:checked + label:before,
  [type="radio"]:not(:checked) + label:before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 25px;
    height: 25px;
    border: 0px solid
      ${(props) =>
        props.mainColor === "accent"
          ? props.theme?.yellowGreen
          : props.theme?.darkPurple};
    border-radius: 100%;
    background: ${(props) =>
      props.mainColor === "accent"
        ? props.theme?.troopGreen
        : props.theme?.dimGray};
  }

  [type="radio"]:checked + label:after,
  [type="radio"]:not(:checked) + label:after {
    content: "";
    width: 25px;
    height: 25px;
    background: ${(props) =>
      props.mainColor === "accent"
        ? props.theme?.yellowGreen
        : props.theme?.darkPurple};
    position: absolute;
    top: 0px;
    left: 0px;
    border-radius: 100%;
    -webkit-transition: all 250ms;
    transition: all 250ms;
  }

  [type="radio"]:not(:checked) + label:after {
    opacity: 0;
  }

  [type="radio"]:checked + label:after {
    opacity: 1;
  }
`;

const RadioCheckLabel = styled.label`
  font-size: 1.1em;
`;

const SitesWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const SiteRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledAutoFocusInput = styled(AutoFocusInput)`
  &.invalid {
    background: ${(props) => props.theme?.fordWhite} !important;
    border-bottom: 2px solid ${(props) => props.theme?.cardinal} !important;
  }
`;

const StyledError = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme?.cardinal};
  text-transform: uppercase;
`;

const AddUserAccessManagement = (props) => {
  const {
    roles,
    studies,
    sites,
    registrationType,
    selectedUser,
    fetchSites,
    onSubmit,
    mainColor,
    submitting,
    onValidateEmail,
    emailExists,
    resetEmailExists,
  } = props;

  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [invalidEmail, setInvalidEmail] = useState({ invalid: false });
  const [selectedRole, setSelectedRole] = useState("");
  const [site, setSite] = useState(null);
  const [availableSites, setAvailableSites] = useState([]);
  const [invalidSite, setInvalidSite] = useState({});

  const showSiteDropdown = useMemo(() => {
    return [profiles.dietitian, profiles.siteCoordinator, profiles.pi].includes(
      selectedRole
    );
  }, [selectedRole]);

  useEffect(() => {
    if (selectedRole) {
      if (
        [
          profiles.itAdmin,
          profiles.siteMonitor,
          profiles.studyAdmin,
          profiles.sponsor,
          profiles.contentAdmin,
          profiles.medicalMonitor,
          profiles.dataManager,
        ].includes(selectedRole)
      ) {
        setSite(Sites.ALL);
      } else if (showSiteDropdown) {
        setSite(null);
      }
    }
  }, [selectedRole, showSiteDropdown]);

  useEffect(() => {
    if (sites) {
      setAvailableSites(sites);
    }
  }, [sites]);

  useEffect(() => {
    if (selectedUser) {
      setEmail(selectedUser.email || selectedUser.userName);
      setSelectedRole(selectedUser.roleName || selectedUser.role);
    } else {
      setEmail("");
      setSelectedRole("");
      setAvailableSites([]);
    }
  }, [selectedUser, fetchSites]);

  useEffect(() => {
    if (selectedUser && availableSites && availableSites.length > 0) {
      if (availableSites[0].value === Sites.ALL.value) {
        setSite(Sites.ALL);
      } else if (selectedUser.sites && selectedUser.sites.length === 1) {
        if (selectedUser.sites[0].name) {
          setSite(
            availableSites.find(
              (temp) => temp.id === selectedUser.sites[0].siteId
            ) ?? null
          );
        }
      } else {
        if (selectedUser.site) {
          setSite({ id: selectedUser.siteId, value: selectedUser.site });
        } else {
          setSite(Sites.ALL);
        }
      }
    }
  }, [selectedUser, availableSites]);

  useEffect(() => {
    if (studies) {
      fetchSites(studies?.studies[0]?.Key);
    } else if (!studies && selectedUser && selectedUser.studies[0]?.studyId) {
      fetchSites(selectedUser.studies[0]?.studyId);
      if (selectedUser.site) {
        setSite({ id: selectedUser.siteId, value: selectedUser.site });
      } else {
        setSite(Sites.ALL);
      }
    }
  }, [fetchSites, selectedUser, studies]);

  useEffect(() => {
    if (emailExists) {
      setInvalidEmail({
        invalid: true,
        message: "The email address you have entered is already registered.",
      });
    }
  }, [emailExists, email]);

  const isSiteValid = useCallback(() => {
    return !!site;
  }, [site]);

  const validateSite = () => {
    const isInvalid = !isSiteValid();
    setInvalidSite({
      invalid: isInvalid,
      message: isInvalid ? t("errorText.fieldRequired") : null,
    });
  };

  const validateEmail = useCallback(
    (value) => {
      const isValid = isEmailValid(value);
      setInvalidEmail({
        invalid: !isValid,
        message: isValid ? null : t("errorText.addNonParticipant.email.valid"),
      });
    },
    [t]
  );

  const onEmailChange = (value) => {
    resetEmailExists();
    validateEmail(value);
    setEmail(value);
  };

  const disabledSubmitButton = () => {
    if (invalidEmail && invalidEmail.invalid) {
      return true;
    }
    if (registrationType === RegistrationType.Admin) {
      return !((email || email !== "") && selectedRole);
    } else {
      return !(email || email !== "");
    }
  };

  const handleSubmit = () => {
    const formData = {
      id: selectedUser?.userId ?? selectedUser?.nonParticipantId,
      email: email,
      roleName: selectedRole,
    };

    if (selectedRole !== profiles.itAdmin) {
      if (
        selectedUser &&
        selectedUser.studies &&
        selectedUser.studies.length > 0
      ) {
        formData.study = selectedUser.studies[0].studyId;
      } else {
        formData.study = studies.studies[0].Key;
      }
    }

    if (
      ![profiles.itAdmin, profiles.contentAdmin].includes(selectedRole) &&
      sites
    ) {
      validateSite();
      if (isSiteValid()) {
        formData.sites = site === Sites.ALL ? [] : [site.id];

        onSubmit(formData);
      }
    } else {
      onSubmit(formData);
    }
  };

  const onChangeRole = (value) => {
    setInvalidSite(null);
    setSelectedRole(value);
  };

  return (
    <Container data-testid="add-user-access-management">
      <Row>
        <div className="col-xl-12">
          <h6>
            {selectedUser
              ? t("appText.addUserAccessManagement.editingUser")
              : t("appText.addUserAccessManagement.addUserEmail")}
          </h6>
          <StyledAutoFocusInput
            data-testid="email-input"
            type="email"
            id="inputEmail"
            className={`form-control ${invalidEmail?.invalid ? "invalid" : ""}`}
            placeholder={t("appText.addUserAccessManagement.email")}
            value={email}
            autoFocus=""
            data-op-id="0"
            onChange={(e) => onEmailChange(e.target.value)}
            onBlur={() =>
              email.length > 0 &&
              selectedUser?.email !== email &&
              selectedUser?.userName !== email &&
              onValidateEmail(email)
            }
            maxLength="128"
          />
          <StyledError>
            {invalidEmail?.invalid && (
              <span className="error-message">{invalidEmail?.message}</span>
            )}
          </StyledError>
        </div>
      </Row>
      <Row className="mt-5">
        {registrationType === RegistrationType.Admin && (
          <Col>
            <h6>{t("appText.addUserAccessManagement.selectRole")}</h6>
            <RolesWrapper className="mt-3" mainColor={mainColor}>
              {roles?.map((role, index) => {
                return (
                  <div
                    key={`role-entry-${index}`}
                    className="custom-admin-control custom-admin-checkbox ps-0"
                  >
                    <input
                      data-testid={`role_${role}`}
                      type="radio"
                      id={`${role}_id`}
                      defaultValue={role}
                      checked={selectedRole === role}
                      onChange={(e) => onChangeRole(e.target.value)}
                    />
                    <RadioCheckLabel htmlFor={`${role}_id`}>
                      {role}
                    </RadioCheckLabel>
                  </div>
                );
              })}
            </RolesWrapper>

            {showSiteDropdown && (
              <SitesWrapper className="mt-4">
                <SiteRow>
                  <h6 className="me-2">
                    {t("appText.addUserAccessManagement.site")}
                  </h6>
                  <CustomDropdown
                    data-testid="site-dropdown"
                    placeholder={t("appText.addUserAccessManagement.select")}
                    value={site?.value}
                    options={availableSites}
                    width={145}
                    onChange={(value) => setSite(value)}
                    limitheight={true}
                  />
                </SiteRow>
                <StyledError className="mt-2">
                  {invalidSite?.invalid && <span>{invalidSite?.message}</span>}
                </StyledError>
              </SitesWrapper>
            )}
          </Col>
        )}
      </Row>

      <ActionButtonsContainer>
        {mainColor === "accent" ? (
          <AccentButton
            data-testid="submit-button"
            onClick={handleSubmit}
            disabled={disabledSubmitButton() || submitting}
          >
            {selectedUser
              ? t("appText.addUserAccessManagement.save")
              : t("appText.addUserAccessManagement.sendInvite")}
          </AccentButton>
        ) : (
          <PrimaryButton
            data-testid="submit-button"
            onClick={handleSubmit}
            disabled={disabledSubmitButton() || submitting}
          >
            {selectedUser
              ? t("appText.addUserAccessManagement.save")
              : t("appText.addUserAccessManagement.sendInvite")}
          </PrimaryButton>
        )}
      </ActionButtonsContainer>
    </Container>
  );
};

AddUserAccessManagement.defaultProps = {
  roles: null,
  studies: null,
  sites: null,
  selectedUser: null,
  mainColor: "accent",
};

AddUserAccessManagement.propTypes = {
  roles: PropTypes.array,
  studies: PropTypes.object,
  sites: PropTypes.array,
  registrationType: PropTypes.oneOf(Object.values(RegistrationType)).isRequired,
  selectedUser: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  mainColor: PropTypes.oneOf(["primary", "accent"]),
};

export default withTheme(AddUserAccessManagement);
